import {
  createApiClient,
  statusOf,
  type AuditEntry,
  type Envelope,
  type Limits,
  type LoginEvent,
  type Me,
  type Paginated,
  type PermissionCatalog,
  type Role,
  type Tenant,
  type User,
} from '@bixo/ui'
import { queryOptions } from '@tanstack/react-query'

let unauthorizedHandler: (() => void) | null = null

/** Registered by the router once it exists, to bounce a 401 to /login. */
export function onUnauthorized(handler: () => void): void {
  unauthorizedHandler = handler
}

export const api = createApiClient({
  baseURL: '/api/v1',
  onUnauthorized: () => unauthorizedHandler?.(),
})

const data = <T>(promise: Promise<{ data: Envelope<T> }>): Promise<T> => promise.then((r) => r.data.data)

/* ------------------------------------------------------------- Payloads */

export interface LoginPayload {
  email: string
  password: string
  remember?: boolean
}

export type LoginResult = { two_factor: false; user: Me } | { two_factor: true }

export interface UserPayload {
  name: string
  email: string
  phone?: string | null
  password?: string
  role_ids: number[]
  is_active: boolean
  limits: Limits
  permissions?: { allow: string[]; deny: string[] }
}

export interface RolePayload {
  name: string
  description?: string | null
  permissions: string[]
}

export interface UsersQuery {
  search?: string
  page?: number
}

export interface AuditQuery {
  action?: string
  user_id?: number
  search?: string
  from?: string
  to?: string
  page?: number
}

export interface TwoFactorSetup {
  secret: string
  secret_formatted: string
  otpauth_url: string
}

/* ------------------------------------------------------------ Endpoints */

export const endpoints = {
  tenant: () => data<Tenant>(api.get('/tenant')),

  login: (body: LoginPayload) => data<LoginResult>(api.post('/auth/login', body)),
  twoFactorChallenge: (body: { code: string } | { recovery_code: string }) =>
    data<{ user: Me }>(api.post('/auth/two-factor-challenge', body)),
  logout: () => api.post('/auth/logout').then(() => undefined),

  me: () => data<Me>(api.get('/me')),
  updateMe: (body: { name: string; phone?: string | null }) => data<Me>(api.patch('/me', body)),
  updatePassword: (body: { current_password: string; password: string; password_confirmation: string }) =>
    api.put('/me/password', body).then(() => undefined),
  startTwoFactor: () => data<TwoFactorSetup>(api.post('/me/two-factor')),
  confirmTwoFactor: (code: string) =>
    data<{ recovery_codes: string[] }>(api.post('/me/two-factor/confirm', { code })),
  regenerateRecoveryCodes: (password: string) =>
    data<{ recovery_codes: string[] }>(api.post('/me/two-factor/recovery-codes', { password })),
  disableTwoFactor: (password: string) =>
    api.delete('/me/two-factor', { data: { password } }).then(() => undefined),
  logins: () => data<LoginEvent[]>(api.get('/me/logins')),

  permissions: () => data<PermissionCatalog>(api.get('/permissions')),
  roles: () => data<Role[]>(api.get('/roles')),
  createRole: (body: RolePayload) => data<Role>(api.post('/roles', body)),
  updateRole: (id: number, body: RolePayload) => data<Role>(api.patch(`/roles/${id}`, body)),
  deleteRole: (id: number) => api.delete(`/roles/${id}`).then(() => undefined),

  users: (params: UsersQuery) => api.get<Paginated<User>>('/users', { params }).then((r) => r.data),
  createUser: (body: UserPayload) => data<User>(api.post('/users', body)),
  updateUser: (id: number, body: UserPayload) => data<User>(api.patch(`/users/${id}`, body)),
  deactivateUser: (id: number) => api.delete(`/users/${id}`).then(() => undefined),

  auditLogs: (params: AuditQuery) =>
    api.get<Paginated<AuditEntry>>('/audit-logs', { params }).then((r) => r.data),
}

/* -------------------------------------------------------------- Queries */

export const qk = {
  tenant: ['tenant'] as const,
  me: ['me'] as const,
  logins: ['me', 'logins'] as const,
  permissions: ['permissions'] as const,
  roles: ['roles'] as const,
  users: (q: UsersQuery) => ['users', q] as const,
  audit: (q: AuditQuery) => ['audit', q] as const,
}

export const tenantQuery = queryOptions({
  queryKey: qk.tenant,
  queryFn: endpoints.tenant,
  staleTime: 10 * 60_000,
  retry: (count, error) => ![404, 423].includes(statusOf(error) ?? 0) && count < 2,
})

/** Resolves to null when signed out, so guards can branch without throwing. */
export const meQuery = queryOptions({
  queryKey: qk.me,
  queryFn: async (): Promise<Me | null> => {
    try {
      return await endpoints.me()
    } catch (error) {
      if (statusOf(error) === 401) return null
      throw error
    }
  },
  staleTime: 5 * 60_000,
  retry: false,
})
